import { UseMutationOptions, useMutation } from "@tanstack/react-query";
import { AxiosRequestConfig } from "axios";

// helpers
import axios from "../axios";
import useUserProfile from "@/hooks/useUserProfile";
import account from "./account";
import { useDelete, useFetch, usePost, usePatch, usePut } from "./reactQuery";
import API_ROUTES from "../routes.constant";
import { pathToUrl } from "./reactQuery/pathToUrl";
import { mutationHooksFactory, TFetcherFunction } from "./reactQuery/mutationHooksFactory";

// interfaces
import { IAbstractEventFormValuesCampaignDetails } from "@/components/AbstractEventForm/IAbstractEventForm";
import { ICampaign, ICampaignItem, ICampaignItemPeriod } from "@/types/api/Campaign";
import { ICampaignSchedules } from "@/types/api/CampaignSchedules";
import { QueryArgsInterface, IUseMutation } from "@/types/api/queryArgs";
import { IPaginatedResponse } from "@/types/api/PaginatedResponse";
import { ILogo } from "@/types/api/Logo";
import { ISpaceFull } from "@/types/Space";
import { INudge } from "@/types/api/Nudge";

type QueryByIdArgs = {
  id: string;
  preview?: string;
} & QueryArgsInterface;

type TUseMutateById<TData = unknown, TVariables = unknown> = {
  id: string | number;
} & Omit<IUseMutation<TData, TVariables>, "slug">;

export default {
  useGetAllCampaigns: (args: QueryArgsInterface) => {
    const { params, config } = args || {};

    return useFetch<IPaginatedResponse<ICampaign>>(API_ROUTES.campaign.campaigns, params, config);
  },
  useAddSpaceToCampaign: (campaignId?: number) => {
    const { data, refetch } = useUserProfile();
    const { mutateAsync } = account.useUpdateProfile();

    return usePost<ICampaignItem>(
      campaignId
        ? pathToUrl(API_ROUTES.campaign.addSpaceToCampaign, { campaignId })
        : null,
      {},
      {
        onSuccess: () => {
          if (data && data.progressBar < 4) {
            mutateAsync({ progressBar: 4 }).then(() => refetch());
          }
        },
      }
    )
  },
  useDeleteSpaceFromCampaign: (args?: TUseMutateById) => {
    const { id, params, config = {} } = args || {};

    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.addSpaceToCampaign, { campaignId: id }) : null;

    return useDelete(fetchKey, params, config);
  },
  
  useCampaign: (args: QueryByIdArgs) => {
    const { id, params, config } = args || {};

    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaign, { id }) : null;

    return useFetch<ICampaign>(fetchKey, params, config);
  },

  useCampaignBookingForm: (args: Omit<QueryByIdArgs, "id"> & { id: number | null }) => {
    const { id, params, config } = args || {};

    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignBookingForm, { id }) : null;

    return useFetch<IAbstractEventFormValuesCampaignDetails>(fetchKey, params, config);
  },

  useQuickCreate: () => {
    const { data, refetch } = useUserProfile();
    const { mutateAsync } = account.useUpdateProfile();

    return usePost<ICampaign>(
      API_ROUTES.campaign.quickCreate,
      {},
      {
        onSuccess: () => {
          if (data && data.progressBar < 3) {
            mutateAsync({ progressBar: 3 }).then(() => refetch());
          }
        },
      }
    );
  },

  useCreateCampaign: () => {
    const { data, refetch } = useUserProfile();
    const { mutateAsync } = account.useUpdateProfile();


    return usePost(
      API_ROUTES.campaign.campaigns,
      {},
      {
        onSuccess: () => {
          if (data && data.progressBar < 3) {
            mutateAsync({ progressBar: 3 }).then(() => refetch());
          }
        },
      }
    )
  },
  useUpdateCampaign: (id: number) => {
    return usePatch(id ? pathToUrl(API_ROUTES.campaign.singleCampaign, { id }) : null);
  },
  useUpdateSingleCampaign: (args?: TUseMutateById) => {
    const { id, params, config } = args || {};
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaign, { id }) : null;

    return usePatch(fetchKey, params, config);
  },
  useDeleteCampaign: () => useDelete(API_ROUTES.campaign.campaigns),

  useAddMembersToCampaign: (id: number) => {
    return usePost(id ? pathToUrl(API_ROUTES.campaign.addMembersToCampaign, { id }) : null);
  },
  useGetShortCampaigns: (args?: QueryArgsInterface) => {
    const { params, config } = args || {};

    return useFetch(API_ROUTES.campaign.shortCampaigns, params, config);
  },
  useCampaignSpaces: (args?: QueryByIdArgs) => {
    const { id, params, config } = args || {};

    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignSpaces, { id }) : null;

    return useFetch<ISpaceFull[]>(fetchKey, params, config);
  },

  useCampaignItems: (args?: QueryByIdArgs) => {
    const { id, params = {}, config } = args || {};

    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignItems, { id }) : null;

    if (!params.ordering) {
      params.ordering = "-added";
    }

    return useFetch<IPaginatedResponse<ICampaignItem>>(fetchKey, params, config);
  },

  useDeleteCampaignItem: (args?: TUseMutateById) => {
    const { id, params, config } = args || {};

    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignItems, { id }) : null;

    return useDelete(fetchKey, params, config);
  },

  usePostCampaignItem: mutationHooksFactory(() => (({ id, data }: { id: number, data: unknown }) => {
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignItems, { id }) : null;

    return axios.post(`${fetchKey}`, data);
  }) as TFetcherFunction),

  useReleaseCampaignOptions: (args: QueryByIdArgs) => {
    const { id, params, config } = args || {};

    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignReleaseAllOptions, { id }) : null;
    
    return useFetch(fetchKey, params, config);
  },

  useCampaignSingleItem: (args?: QueryByIdArgs & { itemId: number }) => {
    const { id, itemId, params, config } = args || {};

    const fetchKey = (id && itemId) ? pathToUrl(API_ROUTES.campaign.singleCampaignSingleItem, { id, itemId }) : null;

    return useFetch<ICampaignItem>(fetchKey, params, config);
  },

  useUpdateCampaignSingleItem: (args?: TUseMutateById & { itemId: number }) => {
    const { id, itemId, params, config } = args || {};

    const fetchKey = (id && itemId) ? pathToUrl(API_ROUTES.campaign.singleCampaignSingleItem, { id, itemId }) : null;

    return usePatch(fetchKey, params, config);
  },

  useDeleteCampaignSingleItem: (args?: TUseMutateById) => {
    const { id, params, config } = args || {};

    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignPeriods, { id }) : null;

    return useDelete(fetchKey, params, config);
  },

  useUpdateCampaignSinglePeriod: (args?: TUseMutateById & { periodId: number }) => {
    const { id, periodId, params, config } = args || {};
    const fetchKey = (id && periodId)
      ? pathToUrl(API_ROUTES.campaign.singleCampaignSinglePeriod, { id, periodId })
      : null;

    return usePut(fetchKey, params, config);
  },

  useCampaignSinglePeriod: (args?: QueryByIdArgs & { periodId: number }) => {
    const { id, periodId, params, config } = args || {};
    const fetchKey = (id && periodId)
      ? pathToUrl(API_ROUTES.campaign.singleCampaignSinglePeriod, { id, periodId })
      : null;

    return useFetch<ICampaignItemPeriod>(fetchKey, params, config);
  },

  useDeleteCampaignPeriod: (args?: TUseMutateById) => {
    const { id, params, config } = args || {};

    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignPeriods, { id }) : null;

    return useDelete(fetchKey, params, config);
  },

  useExportCampaignPack: mutationHooksFactory(() => (
    ({ id, data, config }: { id: number; data: unknown; config: AxiosRequestConfig; }) => {
      const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignSpacesExport, { id }) : null;

      return axios.post(fetchKey as string, data, config);
    }) as TFetcherFunction),

  useBrandLogo: (args?: QueryByIdArgs) => {
    const { id, params, config } = args || {};
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.brandLogos, { id }) : null;

    return useFetch<IPaginatedResponse<ILogo>>(fetchKey, params, config);
  },

  useAgencyLogo: (args?: QueryByIdArgs) => {
    const { id, params, config } = args || {};
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.agencyLogos, { id }) : null;

    return useFetch<IPaginatedResponse<ILogo>>(fetchKey, params, config);
  },

  usePostBrandLogo: (args?: TUseMutateById<{ imageUrl: string }>) => {
    const { id, params, config } = args || {};
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.brandLogos, { id }) : null;

    return usePost<{ imageUrl: string }>(fetchKey, params, config);
  },

  usePostAgencyLogo: (args?: TUseMutateById<{ imageUrl: string }>) => {
    const { id, params, config } = args || {};
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.agencyLogos, { id }) : null;

    return usePost<{ imageUrl: string }>(fetchKey, params, config);
  },

  useDeleteBrandLogo: (args?: TUseMutateById) => {
    const { id, params, config } = args || {};
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.brandLogos, { id }) : null;

    return useDelete(fetchKey, params, config);
  },

  useDeleteAgencyLogo: (args?: TUseMutateById) => {
    const { id, params, config } = args || {};
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.agencyLogos, { id }) : null;

    return useDelete(fetchKey, params, config);
  },

  useCampaignSchedules: (args?: QueryByIdArgs) => {
    const { id, params, config } = args || {};
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignSchedules, { id }) : null;

    return useFetch<IPaginatedResponse<ICampaignSchedules>>(fetchKey, params, config);
  },

  useDeleteCampaignSchedule: (args?: TUseMutateById) => {
    const { id, params, config } = args || {};
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignSchedules, { id }) : null;

    return useDelete(fetchKey, params, config);
  },


  useCampaignSingleSchedules: (args?: QueryByIdArgs & { scheduleId: number }) => {
    const { id, scheduleId, params, config } = args || {};
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignSingleSchedules, { id, scheduleId }) : null;

    return useFetch(fetchKey, params, config);
  },


  usePostCampaignSchedules: (args?: TUseMutateById<{ id: number }>) => {
    const { id, params, config } = args || {};
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignSchedules, { id }) : null;

    return usePost<{ id: number }>(fetchKey, params, config);
  },

  useDeleteCampaignSchedules: (args?: TUseMutateById) => {
    const { id, params, config } = args || {};
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignSchedules, { id }) : null;

    return useDelete(fetchKey, params, config);
  },

  useBulkDeleteCampaignActivities: (args?: TUseMutateById) => {
    const { id, params, config } = args || {};
    const fetchKey = id ? pathToUrl(API_ROUTES.campaign.singleCampaignBulkDelete, { id }) : null;

    return usePost(fetchKey, params, config);
  },

  useCampaignSingleNudge: (args?: QueryByIdArgs & { nudgeId: number }) => {
    const { id, nudgeId, params, config } = args || {};
    const fetchKey = id && nudgeId ? pathToUrl(API_ROUTES.campaign.singleCampaignNudge, { id, nudgeId }) : null;

    return useFetch<INudge>(fetchKey, params, config);
  },

  usePatchNudgeUpdate: (args?: TUseMutateById & { nudgeId: number }) => {
    const { id, nudgeId, params, config } = args || {};
    const fetchKey = id && nudgeId ? pathToUrl(API_ROUTES.campaign.singleCampaignNudge, { id, nudgeId }) : null;

    return usePatch(fetchKey, params, config);
  },

  useUpdateCampaignSingleSchedules: mutationHooksFactory(() => (
    ({ id, scheduleId, data }: { id: unknown, scheduleId: unknown, data: unknown }) => {
      const fetchKey = id && scheduleId
        ? pathToUrl(API_ROUTES.campaign.singleCampaignSingleSchedules, { id, scheduleId })
        : null;

      return axios.put(fetchKey as string, data);
    }) as TFetcherFunction),

  useUpdateCampaignScheduleItems: mutationHooksFactory(() => (
    ({ id, scheduleId, data }: { id: unknown, scheduleId: unknown, data: unknown }) => {
      const fetchKey = id && scheduleId
        ? pathToUrl(API_ROUTES.campaign.singleCampaignSingleSchedulesItems, { id, scheduleId })
        : null;

      return axios.put(fetchKey as string, data);
    }) as TFetcherFunction),

  useDeleteCampaignScheduleItem: (args?: { scheduleId?: string } & TUseMutateById) => {
    type TVariables = { spaces: number[] };

    const { id, scheduleId, params, config } = args || {};

    const fetchKey = id && scheduleId
      ? pathToUrl(API_ROUTES.campaign.singleCampaignSingleSchedulesItems, { id, scheduleId })
      : null;

    return useMutation<TVariables, Error, TVariables>({
      mutationFn: ((data: TVariables) => {

        return axios.delete(fetchKey as string, { data });
      }),
      mutationKey: [API_ROUTES.campaign.singleCampaignSingleSchedulesItems, params],
      enabled: false,
      ...(config || {})
    } as UseMutationOptions<TVariables, Error, TVariables>);
  },
};
